/**
 * @mhmo91/ican — `when(cond).allow/deny(...actions)`.
 *
 * `when` is exclusively reached via `entity().pipe(({ when, … }) =>
 * [...])`. The pipe builder constructs a per-entity `when` (via
 * `makeWhen` below) whose callback proxies are typed against the
 * entity's schema and actor — TS doesn't flow contextual types
 * through method chains, so the closure is the cleanest path to keep
 * `data.foo` / `me.role` narrowed inside each rule.
 *
 * The callback is *not* invoked at `when()` time. The entity's
 * `.pipe(...)` runs it once with fresh recording proxies and freezes
 * the resulting AST onto the rule.
 */
import type { z } from 'zod';
import type {
  UserBase,
  UserProxy,
  Cond,
  DataProxy,
  Rule,
  WritePattern,
  WriteValue,
} from './types.ts';

export const PENDING_BRAND: unique symbol = Symbol.for('@mhmo91/ican/pending');

/**
 * Pending pre-state predicate carried by a `.writes(...)` builder.
 * Resolved at `entity.pipe` finalize time alongside the main `_callback`,
 * using the same recording proxies, then frozen onto the `WritePattern`
 * as a `Cond`.
 */
export type PendingPreState<Schema extends z.ZodTypeAny> =
  | undefined
  | ((data: DataProxy<Schema>) => Cond);

/**
 * Pending write-pattern declaration. Mirrors `WritePattern` but defers
 * pre-state resolution until pipe finalize time (so the recording-
 * proxy infrastructure can capture the AST exactly once, alongside
 * the main rule condition).
 */
export type PendingWritePattern<Schema extends z.ZodTypeAny> =
  | {
      readonly kind: 'update';
      readonly values: Readonly<Record<string, import('./types.ts').WriteValue>>;
      readonly preStateCallback?: PendingPreState<Schema>;
    }
  | {
      readonly kind: 'updateMask';
      readonly fields: ReadonlyArray<keyof z.infer<Schema> & string>;
      readonly pinnedValues?: Readonly<Record<string, import('./types.ts').WriteValue>>;
      readonly preStateCallback?: PendingPreState<Schema>;
    }
  | {
      readonly kind: 'set';
      readonly values: Readonly<Record<string, import('./types.ts').WriteValue>>;
      readonly fields?: ReadonlyArray<keyof z.infer<Schema> & string>;
      readonly requiredFields?: ReadonlyArray<keyof z.infer<Schema> & string>;
    }
  | {
      readonly kind: 'changes';
      readonly fields: ReadonlyArray<keyof z.infer<Schema> & string>;
      readonly pinnedValues?: Readonly<Record<string, import('./types.ts').WriteValue>>;
      readonly postStateCallback?: PendingPreState<Schema>;
    }
  | { readonly kind: 'delete'; readonly preStateCallback?: PendingPreState<Schema> };

/**
 * Internal carrier — pairs the rule's effect/actions with the
 * deferred condition callback. `entity.pipe` recognizes this brand
 * and consumes the callback at finalize time, replacing the seed
 * with a finalized `Rule` whose `cond` is the frozen AST.
 *
 * `pendingWritePattern` and `requiresHandler` are populated by the
 * `can(verb).when(cond).writes(...)` and `.requiresHandler(name)`
 * chain respectively (see `./can.ts`); absent for plain
 * `when(cond).allow(...)` rules.
 */
export type PendingRule<User extends UserBase, Schema extends z.ZodTypeAny, A extends string> = {
  readonly effect: 'allow' | 'deny';
  readonly actions: ReadonlyArray<A>;
  readonly [PENDING_BRAND]: true;
  readonly _callback: (data: DataProxy<Schema>, me: UserProxy<User>) => Cond;
  readonly pendingWritePattern?: PendingWritePattern<Schema>;
  readonly handlerName?: string;
};

/**
 * Resolve a `PendingWritePattern` to a finalized `WritePattern` by
 * running the pre-state callback (if any) against the supplied
 * recording proxy and freezing the resulting AST.
 */
export const finalizePendingWritePattern = <Schema extends z.ZodTypeAny>(
  pending: PendingWritePattern<Schema>,
  dataProxy: DataProxy<Schema>,
  freeze: <T>(v: T) => T,
): WritePattern => {
  if (pending.kind === 'update') {
    const values = freeze({ ...pending.values });
    if (pending.preStateCallback) {
      const preState = freeze<Cond>(pending.preStateCallback(dataProxy));
      return Object.freeze({ kind: 'update' as const, values, preState });
    }
    return Object.freeze({ kind: 'update' as const, values });
  }
  if (pending.kind === 'updateMask') {
    const fields = freeze([...pending.fields] as ReadonlyArray<string>);
    const pins = pending.pinnedValues ? { values: freeze({ ...pending.pinnedValues }) } : {};
    if (pending.preStateCallback) {
      const preState = freeze<Cond>(pending.preStateCallback(dataProxy));
      return Object.freeze({ kind: 'updateMask' as const, fields, ...pins, preState });
    }
    return Object.freeze({ kind: 'updateMask' as const, fields, ...pins });
  }
  if (pending.kind === 'set') {
    const values = freeze({ ...pending.values });
    if (pending.fields) {
      const fields = freeze([...pending.fields] as ReadonlyArray<string>);
      const required = pending.requiredFields
        ? { requiredFields: freeze([...pending.requiredFields]) }
        : {};
      return Object.freeze({ kind: 'set' as const, values, fields, ...required });
    }
    return Object.freeze({ kind: 'set' as const, values });
  }
  if (pending.kind === 'changes') {
    const fields = freeze([...pending.fields] as ReadonlyArray<string>);
    return Object.freeze({
      kind: 'changes' as const,
      fields,
      ...(pending.pinnedValues ? { values: freeze({ ...pending.pinnedValues }) } : {}),
      ...(pending.postStateCallback
        ? { postState: freeze<Cond>(pending.postStateCallback(dataProxy)) }
        : {}),
    });
  }
  if (pending.preStateCallback) {
    const preState = freeze<Cond>(pending.preStateCallback(dataProxy));
    return Object.freeze({ kind: 'delete' as const, preState });
  }
  return Object.freeze({ kind: 'delete' as const });
};

/**
 * The `w` argument inside a deny rule's `.writes(w => ...)`. It offers
 * `changes(...)` and nothing else, because every other write pattern is
 * positive-polarity and a deny composed from one denies the wrong set: a
 * `hasOnly` mask on a deny fires when the patch touches ONLY the listed
 * fields and stands aside when it touches them alongside anything else.
 * The allow side's factory (`can(verb).when(...).writes(...)`, `./can.ts`)
 * is where the positive patterns live.
 */
export type DenyWritePatternFactory<Schema extends z.ZodTypeAny> = {
  /**
   * Narrow the deny to updates that CHANGE at least one of the named
   * fields, reserving them to the authority the rule's own condition
   * names. The list is typed against `keyof z.infer<Schema>`, so renaming
   * or dropping one of them fails `tsc` here.
   */
  changes(fields: ReadonlyArray<keyof z.infer<Schema> & string>): ChangesPatternBuilder<Schema>;
};

/**
 * Guarded-field builder. `.values(...)` pins the shape the patch LANDS on,
 * in the tokens `.update` / `.set` take, giving the deny its second plane:
 * the rule's own `cond` reads the stored document, the pins read the one the
 * write produces, and the refusal names a transition rather than a state.
 * A pinned field need not be one of the guarded fields — the list is the
 * trigger, the pins are the landing shape, and refusing a second payment mark
 * keys on `paidAt` moving while pinning `paid` to the value that tells a
 * re-mark from the un-mark that clears it.
 */
export type ChangesPatternBuilder<Schema extends z.ZodTypeAny> = PendingWritePattern<Schema> & {
  readonly kind: 'changes';
  values(values: Readonly<Record<string, WriteValue>>): ChangesPatternBuilder<Schema>;
  /** Narrow the refusal to the proposed document's condition. */
  to(cond: (data: DataProxy<Schema>) => Cond): ChangesPatternBuilder<Schema>;
};

const buildChanges = <Schema extends z.ZodTypeAny>(
  fields: ReadonlyArray<keyof z.infer<Schema> & string>,
  pinnedValues?: Readonly<Record<string, WriteValue>>,
  postStateCallback?: PendingPreState<Schema>,
): ChangesPatternBuilder<Schema> => ({
  kind: 'changes',
  fields,
  ...(pinnedValues ? { pinnedValues } : {}),
  ...(postStateCallback ? { postStateCallback } : {}),
  values(next) {
    if (pinnedValues !== undefined) {
      throw new Error(
        '@mhmo91/ican: changes(...).values(...) is already set — a second call would replace the first pins and NARROW the deny; pass every pinned field in one map',
      );
    }
    if (Object.keys(next).length === 0) {
      throw new Error(
        '@mhmo91/ican: changes(...).values({}) pins nothing — omit .values(...) for a deny that reads the stored plane alone',
      );
    }
    return buildChanges<Schema>(fields, next, postStateCallback);
  },
  to(cond) {
    if (postStateCallback !== undefined) {
      throw new Error(
        '@mhmo91/ican: changes(...).to(...) is already set — combine post-state predicates in one condition',
      );
    }
    return buildChanges<Schema>(fields, pinnedValues, cond);
  },
});

/**
 * `when(cond).deny(...actions)` — a `Rule` that additionally accepts a
 * `.writes(...)` narrowing. Without one the deny covers the whole action;
 * with one it covers only the patches the pattern matches.
 */
export type DenyChain<User extends UserBase, Schema extends z.ZodTypeAny, A extends string> = Rule<
  User,
  z.infer<Schema>,
  A
> & {
  writes(
    build: (w: DenyWritePatternFactory<Schema>) => PendingWritePattern<Schema>,
  ): Rule<User, z.infer<Schema>, A>;
};

/**
 * Build a `when(...)` factory typed against the given actor + schema.
 * Called once per entity by `entity.pipe`'s builder context; the
 * resulting factory is handed to the rule author through the
 * destructured `{ when }` parameter.
 */
export const makeWhen = <
  User extends UserBase,
  Schema extends z.ZodTypeAny,
  Actions extends readonly string[],
>() => {
  function whenFn(callback: (data: DataProxy<Schema>, me: UserProxy<User>) => Cond): {
    allow<As extends ReadonlyArray<Actions[number]>>(
      ...actions: As
    ): Rule<User, z.infer<Schema>, As[number]>;
    deny<As extends ReadonlyArray<Actions[number]>>(
      ...actions: As
    ): DenyChain<User, Schema, As[number]>;
  } {
    const seedFor = <As extends ReadonlyArray<Actions[number]>>(
      effect: 'allow' | 'deny',
      actions: As,
    ): PendingRule<User, Schema, As[number]> => ({
      effect,
      actions,
      [PENDING_BRAND]: true,
      _callback: callback,
    });
    return {
      allow: <As extends ReadonlyArray<Actions[number]>>(...actions: As) =>
        seedFor('allow', actions) as unknown as Rule<User, z.infer<Schema>, As[number]>,
      deny: <As extends ReadonlyArray<Actions[number]>>(...actions: As) => {
        const seed = seedFor('deny', actions);
        return {
          ...seed,
          writes(build: (w: DenyWritePatternFactory<Schema>) => PendingWritePattern<Schema>) {
            return Object.freeze({
              ...seed,
              pendingWritePattern: build({ changes: (fields) => buildChanges<Schema>(fields) }),
            }) as unknown as Rule<User, z.infer<Schema>, As[number]>;
          },
        } as unknown as DenyChain<User, Schema, As[number]>;
      },
    };
  }
  return whenFn;
};

export const isPendingRule = (
  value: unknown,
): value is PendingRule<UserBase, z.ZodTypeAny, string> =>
  typeof value === 'object' && value !== null && PENDING_BRAND in value;
